import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useCredits } from '../../contexts/CreditsContext';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { Card, CardContent, CardHeader } from '../ui/Card';
import { Button } from '../ui/Button';
import { 
  CreditCard, 
  Zap, 
  CheckCircle, 
  AlertCircle, 
  Wallet,
  Crown,
  Sparkles,
  TrendingUp
} from 'lucide-react';

// Default credit packages (will be replaced by API response if available)
const DEFAULT_CREDIT_PACKAGES = [
  { 
    id: 'plan1', 
    name: '50,000 Credits', 
    price: 49.99, 
    credits: 50000,
    popular: true,
    perCredit: 0.001
  },
  { 
    id: 'plan2', 
    name: '20,000 Credits', 
    price: 19.99, 
    credits: 20000,
    popular: false,
    perCredit: 0.001
  },
  { 
    id: 'plan3', 
    name: '10,000 Credits', 
    price: 9.99, 
    credits: 10000,
    popular: false,
    perCredit: 0.001
  },
  { 
    id: 'plan4', 
    name: '5,000 Credits', 
    price: 4.99, 
    credits: 5000,
    popular: false,
    perCredit: 0.001
  },
  { 
    id: 'plan5', 
    name: '2,000 Credits', 
    price: 1.99, 
    credits: 2000,
    popular: false,
    perCredit: 0.001
  },
];

const PaymentPage = () => {
  const { currentUser, isAnonymous } = useAuth();
  const { credits, loading } = useCredits();
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [plansLoading, setPlansLoading] = useState(true);
  const [plans, setPlans] = useState(DEFAULT_CREDIT_PACKAGES);

  // PayPal script options
  const paypalOptions = {
    "client-id": process.env.REACT_APP_PAYPAL_CLIENT_ID || "sb", // Use sandbox client ID for development
    currency: "USD",
    intent: "capture",
  };

  // Fetch plans from backend
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await fetch('/api/credits/plans');
        const data = await response.json();
        
        if (data.success && data.data.plans) {
          // Map the plans to include the enhanced features
          const enhancedPlans = data.data.plans.map((plan, index) => ({
            ...plan,
            ...DEFAULT_CREDIT_PACKAGES[index] // Merge with default enhanced data
          }));
          setPlans(enhancedPlans);
        }
      } catch (error) {
        console.error('Error fetching plans:', error);
        // Fall back to default plans if API fails
      } finally {
        setPlansLoading(false);
      }
    };

    fetchPlans();
  }, []);

  const handlePurchaseSuccess = async (details) => {
    try {
      // Call your server to save the transaction
      const response = await fetch('/api/credits/purchase', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await currentUser.getIdToken()}`
        },
        body: JSON.stringify({
          orderId: details.id,
          planId: selectedPlan.id,
          amount: selectedPlan.credits,
          transactionDetails: details
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setPaymentSuccess(true);
        setSelectedPlan(null);
      } else {
        setPaymentError(data.message || 'Failed to process payment');
      }
    } catch (error) {
      console.error('Error processing payment:', error);
      setPaymentError('Failed to process payment. Please try again.');
    }
  };

  const formatNumber = (num) => {
    return num.toLocaleString();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header Section */}
        <div className="text-center mb-16">
          <div className="flex justify-center mb-6">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-primary to-secondary rounded-full blur-xl opacity-20"></div>
              <div className="relative bg-gradient-to-r from-primary to-secondary p-4 rounded-full">
                <CreditCard className="h-8 w-8 text-white" />
              </div>
            </div>
          </div>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6">
            Choose Your 
            <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent"> Credit Plan</span>
          </h1>
        </div>

        {/* Alert Messages */}
        {isAnonymous && (
          <Card className="mb-8 max-w-2xl mx-auto border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/20">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 text-center justify-center">
                <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
                <p className="text-amber-800 dark:text-amber-300 font-medium">
                  Please sign in to purchase credits and unlock all features.
                </p>
              </div>
            </CardContent>
          </Card>
        )}
        
        {paymentSuccess && (
          <Card className="mb-8 max-w-2xl mx-auto border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950/20">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 text-center justify-center">
                <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                <p className="text-green-800 dark:text-green-300 font-medium">
                  🎉 Payment successful! Your credits have been added to your account.
                </p>
              </div>
            </CardContent>
          </Card>
        )}
        
        {paymentError && (
          <Card className="mb-8 max-w-2xl mx-auto border-destructive/20 bg-destructive/5">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 text-center justify-center">
                <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0" />
                <p className="text-destructive font-medium">{paymentError}</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Current Balance Card */}
        <Card className="mb-12 max-w-md mx-auto bg-gradient-to-r from-card to-card/50 border-2">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Wallet className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Current Balance</p>
                  <p className="text-2xl font-bold text-foreground">
                    {loading ? (
                      <span className="animate-pulse">Loading...</span>
                    ) : (
                      `${formatNumber(credits)} credits`
                    )}
                  </p>
                </div>
              </div>
              <Sparkles className="h-6 w-6 text-primary/50" />
            </div>
          </CardContent>
        </Card>
        
        {/* Pricing Plans */}
        {plansLoading ? (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="relative">
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-primary/20 border-t-primary"></div>
              <div className="absolute inset-0 rounded-full bg-gradient-to-r from-primary/20 to-secondary/20 blur-sm"></div>
            </div>
            <p className="mt-4 text-muted-foreground">Loading credit plans...</p>
          </div>
        ) : (
          <div className="grid gap-6 md:gap-8 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 mb-12">
            {plans.map((plan, index) => (
              <Card 
                key={plan.id}
                className={`relative overflow-hidden transition-all duration-300 cursor-pointer group hover:shadow-2xl hover:shadow-primary/25 ${
                  selectedPlan?.id === plan.id 
                    ? 'ring-2 ring-primary shadow-2xl shadow-primary/25 scale-105' 
                    : 'hover:scale-105'
                } ${
                  plan.popular 
                    ? 'border-primary/50 bg-gradient-to-b from-primary/5 to-background' 
                    : 'border-border hover:border-primary/30'
                }`}
                onClick={() => !isAnonymous && setSelectedPlan(plan)}
              >
                {plan.popular && (
                  <div className="absolute top-0 left-0 right-0">
                    <div className="bg-gradient-to-r from-primary to-secondary text-white text-center py-2 text-sm font-semibold">
                      <div className="flex items-center justify-center gap-1">
                        <Crown className="h-4 w-4" />
                        MOST POPULAR
                      </div>
                    </div>
                  </div>
                )}
                
                <CardHeader className={`pb-4 ${plan.popular ? 'pt-12' : 'pt-6'}`}>
                  <div className="text-center">
                    <h3 className="text-xl font-bold text-foreground mb-2">{plan.name}</h3>
                    
                    <div className="mb-4">
                      <span className="text-4xl font-bold text-foreground">${plan.price.toFixed(2)}</span>
                      <span className="text-muted-foreground ml-1">USD</span>
                    </div>
                    
                    <div className="flex items-center justify-center gap-1 text-sm text-muted-foreground">
                      <TrendingUp className="h-4 w-4" />
                      <span>{formatNumber(plan.credits)} credits</span>
                    </div>
                  </div>
                </CardHeader>

                <CardContent className="pt-0 pb-6">
                  <Button 
                    className={`w-full font-semibold transition-all duration-200 ${
                      selectedPlan?.id === plan.id 
                        ? 'bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg' 
                        : plan.popular
                        ? 'bg-primary hover:bg-primary/90 text-primary-foreground'
                        : 'bg-secondary hover:bg-secondary/80 text-secondary-foreground'
                    }`}
                    disabled={isAnonymous}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedPlan(plan);
                    }}
                  >
                    <div className="flex items-center justify-center gap-2">
                      {selectedPlan?.id === plan.id ? (
                        <>
                          <CheckCircle className="h-4 w-4" />
                          Selected
                        </>
                      ) : (
                        <>
                          <Zap className="h-4 w-4" />
                          Select Plan
                        </>
                      )}
                    </div>
                  </Button>
                </CardContent>

                {/* Hover effect overlay */}
                <div className="absolute inset-0 bg-gradient-to-r from-primary/5 to-secondary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
              </Card>
            ))}
          </div>
        )}
        
        {/* Payment Section */}
        {selectedPlan && !isAnonymous && (
          <Card className="max-w-lg mx-auto bg-gradient-to-br from-card to-card/50 border-2 border-primary/20">
            <CardHeader className="text-center pb-4">
              <div className="flex justify-center mb-4">
                <div className="p-3 bg-primary/10 rounded-full">
                  <CreditCard className="h-6 w-6 text-primary" />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-foreground">Complete Your Purchase</h3>
              <p className="text-muted-foreground">Secure payment powered by PayPal</p>
            </CardHeader>
            
            <CardContent className="space-y-6">
              {/* Order Summary */}
              <div className="bg-muted/30 rounded-lg p-4 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Package:</span>
                  <span className="font-semibold text-foreground">{selectedPlan.name}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Credits:</span>
                  <span className="font-semibold text-foreground">{formatNumber(selectedPlan.credits)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Price:</span>
                  <span className="font-semibold text-foreground">${selectedPlan.price.toFixed(2)} USD</span>
                </div>
                <div className="border-t border-border pt-3 flex justify-between items-center">
                  <span className="font-semibold text-foreground text-lg">Total:</span>
                  <span className="font-bold text-foreground text-xl">${selectedPlan.price.toFixed(2)} USD</span>
                </div>
              </div>
              
              {/* PayPal Payment */}
              <div className="space-y-4">
                <PayPalScriptProvider options={paypalOptions}>
                  <PayPalButtons
                    createOrder={(data, actions) => {
                      return actions.order.create({
                        purchase_units: [
                          {
                            description: `${selectedPlan.name} for AI Humanizer`,
                            amount: {
                              value: selectedPlan.price.toFixed(2),
                            },
                          },
                        ],
                      });
                    }}
                    onApprove={(data, actions) => {
                      return actions.order.capture().then((details) => {
                        handlePurchaseSuccess(details);
                      });
                    }}
                    onError={(err) => {
                      console.error('PayPal Error:', err);
                      setPaymentError('PayPal payment failed. Please try again.');
                    }}
                    style={{
                      layout: 'vertical',
                      color: 'blue',
                      shape: 'rect',
                      label: 'pay',
                      height: 45,
                    }}
                  />
                </PayPalScriptProvider>
                
                <Button 
                  variant="ghost"
                  className="w-full text-muted-foreground hover:text-foreground"
                  onClick={() => setSelectedPlan(null)}
                >
                  Cancel Selection
                </Button>
              </div>

              {/* Security Notice */}
              <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
                <CheckCircle className="h-4 w-4 text-green-500" />
                <span>Secure SSL encrypted payment</span>
              </div>
            </CardContent>
          </Card>
        )}
        
      </div>
    </div>
  );
};

export default PaymentPage; 